import type { CustomFieldRow } from "@cx/contracts";
import { Field } from "../form/Field";

type Props = {
  fields: CustomFieldRow[];
  values: Record<string, string>;
  onChange(values: Record<string, string>): void;
  errors?: Record<string, string>;
};

/** Valor de multiselect guardado como JSON; valor corrompido vira lista vazia em vez de derrubar a tela. */
const parseList = (v: string): string[] => {
  try {
    const a: unknown = JSON.parse(v);
    return Array.isArray(a) ? a.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
};

export function CustomFieldsPanel({ fields, values, onChange, errors = {} }: Props) {
  const set = (id: string, v: string) => onChange({ ...values, [id]: v });
  const active = fields.filter((f) => f.active);
  if (!active.length)
    return (
      <p className="text-muted">Nenhum campo customizado. Crie em Preferências › Campos customizados.</p>
    );
  return (
    <div className="row">
      {active.map((f) => {
        const v = values[f.id] ?? "";
        const control =
          f.fieldType === "select" ? (
            <select className="form-select" value={v} onChange={(e) => set(f.id, e.target.value)}>
              <option value="">Selecione</option>
              {f.options?.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          ) : f.fieldType === "boolean" ? (
            <select className="form-select" value={v} onChange={(e) => set(f.id, e.target.value)}>
              <option value="">Não informado</option>
              <option value="true">Sim</option>
              <option value="false">Não</option>
            </select>
          ) : f.fieldType === "multiselect" ? (
            <select
              multiple
              className="form-select"
              value={v ? parseList(v) : []}
              onChange={(e) => set(f.id, JSON.stringify([...e.target.selectedOptions].map((o) => o.value)))}
            >
              {f.options?.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          ) : (
            <input
              type={f.fieldType === "date" ? "date" : f.fieldType === "url" ? "url" : "text"}
              inputMode={f.fieldType === "number" || f.fieldType === "decimal" ? "decimal" : undefined}
              value={v}
              onChange={(e) => set(f.id, e.target.value)}
            />
          );
        return (
          <Field key={f.id} label={f.label} required={f.required} error={errors[`customFields.${f.id}`]}>
            {control}
          </Field>
        );
      })}
    </div>
  );
}
